import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { AlertService } from "../shared/services/alert.service";

interface StatusBody {
  status?: number;
  message?: string;
  severity?: string;
  data?: unknown;
}

export interface IApiResponse<T = unknown> {
  success: boolean;
  message: string;
  severity: "success" | "error" | string;
  error: T;
}

@Injectable({ providedIn: "root" })
export class ApiResponseInterceptor implements HttpInterceptor {
  constructor(
    private alertService: AlertService,
    private router: Router
  ) { }

  private skipRoutesUrl: string[] = [
    "viacep.com.br",
    "/notification/actives",
    "/parameter/",
    "/state",
    "/municipality/"
  ]

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      map((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          if (req.headers.has("X-Transform-Response") || req.params.has("transform")) {
            const response = event.body as StatusBody | null;
            const transformed: IApiResponse =
              response && response.status
                ? this.handleStatus(response.status, response)
                : {
                  success: true,
                  message: response?.message || "Operação realizada com sucesso.",
                  severity: response?.severity || "success",
                  error: response,
                };
            return event.clone({ body: transformed });
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return throwError(() => error);
        }

        let message = error.error?.errors?.[error.error.title]
          || error.error?.details
          || error.error?.error
          || error.message
          ;

        let title = "Erro Inesperado";
        switch (error.status) {
          case 400:
            title = "Dados Inválidos";
            message = message || "Verifique os dados enviados e tente novamente.";
            break;
          case 403:
            title = "Acesso Negado";
            message = "Não tem permissão para realizar esta ação.";
            this.router.navigate(["/home"]);
            break;
          case 404:
            title = "Recurso não Encontrado";
            message = "Verifique se esses dados estão corretos.";
            break;
          case 405:
            title = "Método não permitido";
            message = "Verifique se o método que você está tentando acessar é permitido.";
            break;
          case 408:
            title = "Tempo expirado";
            message = "Verifique a conexão com a internet";
            break;
          case 413:
            title = "Muitas informações";
            message = "Informações enviadas a mais do que o necessário";
            break;
          case 429:
            title = "Muitas requisições";
            message = "Tente novamente mais tarde";
            break;
          case 500:
            title = "Erro no Servidor";
            break;
        }

        if (!this.shouldSkipAlert(req, error)) {
          const messageWithStatusError = `${message} - [${error.status}]`
          // this.alertService.error(messageWithStatusError, {
          //   title: title,
          // });
        }

        const transformed: IApiResponse = error.status
          ? this.handleErrorStatus(error.status, error.error)
          : {
            success: false,
            message: "Ocorreu um erro desconhecido.",
            severity: "error",
            error: error,
          };

        return throwError(() => transformed);
      }),
    );
  }

  private handleStatus(status: number, response: StatusBody): IApiResponse {
    switch (status) {
      case 200:
      case 201:
      case 204:
        return {
          success: true,
          message: response?.message || "Operação realizada com sucesso.",
          severity: "success",
          error: response.data || response,
        };
      case 400:
        return {
          success: false,
          message: response?.message || "Requisição Inválida",
          severity: "error",
          error: response.data,
        };
      case 401:
        return {
          success: false,
          message: response?.message || "Não Autorizado",
          severity: "error",
          error: response.data,
        };
      case 403:
        return {
          success: false,
          message: response?.message || "Proibido",
          severity: "error",
          error: response.data,
        };
      case 404:
        return {
          success: false,
          message: response?.message || "Não Encontrado",
          severity: "error",
          error: response.data,
        };
      case 500:
        return {
          success: false,
          message: response?.message || "Erro desconhecido no servidor.",
          severity: "error",
          error: response.data || "Erro desconhecido no servidor.",
        };
      default:
        return {
          success: false,
          message: response?.message || `Erro inesperado: ${status}`,
          severity: "error",
          error: response.data,
        };
    }
  }

  private handleErrorStatus(status: number, errorData: StatusBody): IApiResponse {
    switch (status) {
      case 400:
        return {
          success: false,
          message: (errorData?.message || errorData || "Requisição Inválida") as string,
          severity: "error",
          error: errorData,
        };
      case 401:
        return {
          success: false,
          message: errorData?.message || "Não Autorizado",
          severity: "error",
          error: errorData,
        };
      case 403:
        return {
          success: false,
          message: errorData?.message || "Proibido",
          severity: "error",
          error: errorData,
        };
      case 404:
        return {
          success: false,
          message: errorData?.message || "Não Encontrado",
          severity: "error",
          error: errorData,
        };
      case 500:
        return {
          success: false,
          message: (errorData?.message || errorData || "Erro desconhecido no servidor.") as string,
          severity: "error",
          error: errorData || "Erro desconhecido no servidor.",
        };
      default:
        return {
          success: false,
          message: errorData?.message || `Erro inesperado: ${status}`,
          severity: "error",
          error: errorData,
        };
    }
  }

  private shouldSkipAlert(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    return error.status === 401 || this.skipRoutesUrl.some(url => req.url.includes(url));
  }
}